/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import isEmpty from "lodash/fp/isEmpty";
import union from "lodash/fp/union";

import { DayTime, Interval } from "~/constants";
import DashboardApi from "~/features/dashboard/DashboardApi";
import transformToGeoLocation from "~/features/dashboard/transformers/transformToGeoLocation";
import transformToPlatformStats from "~/features/dashboard/transformers/transformToPlatformStats";
import transformToStats from "~/features/dashboard/transformers/transformToStats";
import {
  PostGeoLocationResponseType,
  PostPlatformStatsResponseType,
  PostStatsPayloadType,
  PostStatsResponseType,
} from "~/features/dashboard/types";
import calculatePeriodStats from "~/features/dashboard/utils/calculatePeriodStats";
import ReportApi from "~/features/reportTracker/ReportApi";
import { ReportRequest } from "~/features/reportTracker/types";
import {
  PostDashboardReportResponseData,
  PostGeoResponseData,
  PostPlatformStatsResponseData,
  PostTotalStatsResponseData,
} from "~/features/statistics/types";
import getDateInterval from "~/utils/getDateInterval";

export const loadQuickStatsAsync = createAsyncThunk("dashboard/loadQuickStats", async () => {
  const lastThirtyDays = getDateInterval(Interval.LAST_30_DAYS);
  const previousPeriod = getDateInterval(Interval.PREV_PERIOD);
  const currentPeriod = getDateInterval(Interval.CURRENT_PERIOD);
  const yesterday = getDateInterval(Interval.YESTERDAY);
  const today = getDateInterval(Interval.TODAY);

  const quickStatsPayload: PostStatsPayloadType = {
    excludedsites: [],
    from: `${lastThirtyDays.from} ${DayTime.START}`,
    to: `${today.to} ${DayTime.END}`,
    groupby: "tm",
    autoload: true,
  };

  const previousPeriodPayload: PostStatsPayloadType = {
    excludedsites: [],
    from: `${previousPeriod.from} ${DayTime.START}`,
    to: `${previousPeriod.to} ${DayTime.END}`,
    groupby: "tm",
    autoload: true,
  };

  const currentPeriodPayload: PostStatsPayloadType = {
    excludedsites: [],
    from: `${currentPeriod.from} ${DayTime.START}`,
    to: `${currentPeriod.to} ${DayTime.END}`,
    groupby: "tm",
    autoload: true,
  };

  const quickStatsPromise = DashboardApi.postStats(quickStatsPayload);
  const previousPeriodPromise = DashboardApi.postStats(previousPeriodPayload);
  const currentPeriodPromise = DashboardApi.postStats(currentPeriodPayload);

  const [quickStatsRes, prevPeriodRes, currPeriodRes] = await Promise.all([
    quickStatsPromise,
    previousPeriodPromise,
    currentPeriodPromise,
  ]);

  const data = quickStatsRes.data.data.body;

  if (!data) {
    return {
      lastThirtyDaysData: {
        netRevenue: 0,
        grossRevenue: 0,
        premiumJoins: 0,
      },
      previousPeriodPayout: 0,
      currentPeriodPayout: 0,
      yesterdayData: {
        netRevenue: 0,
        grossRevenue: 0,
        premiumJoins: 0,
      },
      todayData: {
        netRevenue: 0,
        grossRevenue: 0,
        premiumJoins: 0,
      },
      payoutProgram: false,
      hasSites: false,
    };
  }

  const lastThirtyDaysData = calculatePeriodStats(data, lastThirtyDays.from, lastThirtyDays.to);
  const yesterdayData = calculatePeriodStats(data, yesterday.from, yesterday.to);
  const todayData = calculatePeriodStats(data, today.from, today.to);

  const previousPeriodPayout = prevPeriodRes.data.data.totals.netpayout;
  const currentPeriodPayout = currPeriodRes.data.data.totals.netpayout;

  //if one of the sites have payout program, display number in "at a galance" sections, else display N/A (Estimated Payout)
  const payoutProgram = data.some((site) => site.pptype !== null && site.pptype !== "");
  const hasSites = data.length > 0;

  return {
    lastThirtyDaysData,
    previousPeriodPayout,
    currentPeriodPayout,
    yesterdayData,
    todayData,
    payoutProgram,
    hasSites,
  };
});

export const loadStatsByIntervalAsync = createAsyncThunk(
  "dashboard/loadStats",
  async (payload: PostStatsPayloadType) => {
    const statsRes = await DashboardApi.postStats(payload);
    const stats = statsRes.data;

    return processTotalStatsData(stats);
  },
);

export const loadSitesAsync = createAsyncThunk("dashboard/loadReferrerSites", async () => {
  const responses = await Promise.all([DashboardApi.getReferrerSites(), DashboardApi.getPromotionSites()]);

  const referrerSites = Object.keys(responses[0].data.sites).map(
    (key) => responses[0].data.sites[Number(key)] as string,
  );
  const promotionSites = responses[1].data.promotions.map((promo) => promo.domain);

  const sites = union(referrerSites, promotionSites);

  return {
    sites: sites,
  };
});

export const loadPlatformStatsAsync = createAsyncThunk(
  "dashboard/loadPlatformStats",
  async (payload: PostStatsPayloadType) => {
    const platformStatsRes = await DashboardApi.postPlatformStats(payload);
    const platformStats = platformStatsRes.data;

    return processPlatformStatsData(platformStats);
  },
);

export const loadGeoLocationAsync = createAsyncThunk(
  "dashboard/loadGrossSummaryData",
  async (payload: PostStatsPayloadType) => {
    const geoLocationRes = await DashboardApi.postGeoReport(payload);
    const countriesData: PostGeoLocationResponseType = geoLocationRes.data;

    return processGeoReportsData(countriesData as PostGeoResponseData);
  },
);

export const loadDashboardReportAsync = createAsyncThunk(
  "report/postLoadDashboardReport",
  async (payload: ReportRequest) => {
    try {
      const res = await ReportApi.postGetReport(payload);
      const { result } = res.data;

      const reports = result.data as PostDashboardReportResponseData | null;

      return {
        status: result.status,
        cacheKey: result.cacheKey,
        totalStats: processTotalStatsData(reports?.totalStatsData),
        platformStats: processPlatformStatsData(reports?.platformStatsData),
        geoReport: processGeoReportsData(reports?.geoReportData),
      };
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      throw new Error((error as any).response.status);
    }
  },
);

const processTotalStatsData = (stats?: PostTotalStatsResponseData) => {
  if (isEmpty(stats) || isEmpty(stats.data)) {
    return {
      byDate: {},
      sortedDates: [],
      sites: [],
    };
  }

  const sites = stats.data.body.map((site) => site.sitedomain).sort((a, b) => a.localeCompare(b));
  const byDate = transformToStats(stats as PostStatsResponseType);
  const sortedDates = Object.keys(byDate).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  return {
    byDate,
    sortedDates,
    sites,
  };
};

const processPlatformStatsData = (platformStats?: PostPlatformStatsResponseData) => {
  if (isEmpty(platformStats)) {
    return {
      devices: {
        byDeviceType: {},
        sortedDeviceTypes: [],
      },
    };
  }

  const byDeviceType = transformToPlatformStats(platformStats as PostPlatformStatsResponseType);
  const sortedDeviceTypes = Object.keys(byDeviceType).sort(
    (a, b) => byDeviceType[b].netRevenue - byDeviceType[a].netRevenue,
  );

  return {
    devices: {
      byDeviceType,
      sortedDeviceTypes,
    },
  };
};

const processGeoReportsData = (countriesData?: PostGeoResponseData) => {
  if (isEmpty(countriesData) || isEmpty(countriesData.data)) {
    return {
      countries: {
        byCountryCode: {},
        sortedCountryCodes: [],
      },
    };
  }

  const byCountryCode = transformToGeoLocation(countriesData as PostGeoLocationResponseType);
  const sortedCountryCodes = Object.keys(byCountryCode).sort(
    (a, b) => byCountryCode[b].netRevenue - byCountryCode[a].netRevenue,
  );

  return {
    countries: {
      byCountryCode,
      sortedCountryCodes,
    },
  };
};
